import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import Config from '@/Management/GlobalConfiguration';
import ResourceManager from '@/Management/ResourceManager';
import type MultilineTextBoxPopUpLinkViewModelType from '@/Components/Links/MultilineTextBoxPopUpLink/MultilineTextBoxPopUpLinkViewModel';

import { IMultilineTextBoxViewModelConstructorParameter } from "#/DefaultViewExtensionInterfaces";

/**
* Das ViewModel für die MultilineTextBox
* @param {object} params - Ein Objekt mit Optionen für das Control
* @constructor
*/
export default class MultilineTextBoxViewModel extends ControlViewModel {

    private MultilineTextBoxPopUpLinkViewModel = ComponentManager.getViewModel<typeof MultilineTextBoxPopUpLinkViewModelType>('MultilineTextBoxPopUpLinkViewModel');

    /**
    * Gibt an, ob die Eingabe in ein mehrzeiliges Textfeld erfolgt
    */
    public isMultiline: KnockoutObservable<boolean>;

    /**
    * Gibt die Größe der Textbox an
    */
    public height: KnockoutObservable<number>;

    /**
    * Der Wert der TextBox
    */
    public value: KnockoutObservable<string>;

    /**
    * Ein Handler, der aufgerufen wird, wenn sich der Wert verändert
    */
    public onChange: (value: string) => void;

    /**
    * Ein Handler, der aufgerufen wird, wenn der Benutzer eine Taste drückt
    */
    public onKeyDown: (keyCode: number) => void;

    /**
    * Ein Handler, der aufgerufen wird, wenn der Benutzer eine Taste loslässt
    */
    public onKeyUp: (keyCode: number) => void;

    /**
    * Ein Handler, der aufgerufen wird, wenn der Benutzer Enter (multiline==false) oder Shift/Ctrl + Enter drückt (multiline==true)
    */
    public onSubmit: (value: string) => void;

    /**
    * Gibt die maximale Länge des Textes an
    */
    public maxLength: number;

    /**
    * Gibt die Sprache des eingegebenen Textes an
    */
    public language: KnockoutObservable<string>;

    /**
     * Aktiviert die option ein PopUp zu verwenden
     */
    public enablePopUp: KnockoutObservable<boolean>;

    private name: string;
    private ergName: string;

    public constructor(params: IMultilineTextBoxViewModelConstructorParameter) {
        super(params);

        this.name =  this.paramOrDefault(params.name);
        this.ergName = this.paramOrDefault(params.ergName);
        this.isMultiline = this.observableOrDefault(params.isMultiline, true);
        this.height = this.observableOrDefault(params.height, Config.multiLineTextBox.DefaultHeight, (value: any) => {
            const int = parseInt(value, 10);
            if (_.isEqual(int, NaN) || int < Config.multiLineTextBox.MinHeight) {
                return Config.multiLineTextBox.DefaultHeight;
            }
            return int;
        });
        this.value = this.observableOrDefault(params.value, '').extend({ notify: 'always' });
        this.onChange = this.handlerOrDefault(params.onChange);
        this.onKeyDown = this.handlerOrDefault(params.onKeyDown);
        this.onKeyUp = this.handlerOrDefault(params.onKeyUp);
        this.onSubmit = this.handlerOrDefault(params.onSubmit);
        this.maxLength = this.paramOrDefault(params.maxLength, -1);
        this.language = this.observableOrDefault(params.language, (ResourceManager.getInstance().language as any).value());
        this.enablePopUp = this.observableOrDefault(this.isMultiline() && params.enablePopUp, false);
     
        ControlViewModel.checkProperties(this, params);
    }

    /**
     * @override 
     * @inheritdoc
     */
    public resetValue(): void {
        this.value(null);
    }

    public createMultilineTextBoxPopUpLinkViewModel(): MultilineTextBoxPopUpLinkViewModelType {
        return new this.MultilineTextBoxPopUpLinkViewModel({
            name: this.name,
            ergName: this.ergName,
            value: this.value,
            isEditingEnabled: this.isEnabled,
            maxLength: this.maxLength,
            parentLocator: this.locator,
            parentTooltip: this.tooltip
        });
    }
}

ComponentManager.setViewModel('MultilineTextBoxViewModel', MultilineTextBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');